import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ViewEncapsulation,
  inject,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { KeycloakService } from 'keycloak-angular';
import { OgcFeatureOlMap, type OgcDrawnGeometry } from '@angular-workspace/feature';
import { OgcFeatureApiService, OgcFeature, OgcFeatureCollection } from '../../../services/ogc-feature-api.service';
import { ToasterService } from '../../../../services/toaster.service';

export type DrawnGeometry = OgcDrawnGeometry;

@Component({
  selector: 'app-ogc-feature-data',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ogc-feature-data.component.html',
  styleUrls: ['./ogc-feature-data.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OgcFeatureDataComponent implements AfterViewInit, OnDestroy {
  @Input() resourceItemData: { id?: string; label?: string; name?: string; ogcServerBaseUrl?: string } | null = null;

  @Output() closeRequested = new EventEmitter<void>();
  @Output() geometryDrawn = new EventEmitter<DrawnGeometry>();

  @ViewChild('featureMap') featureMapRef!: ElementRef<HTMLDivElement>;

  private keycloak = inject(KeycloakService);
  private ogcApi = inject(OgcFeatureApiService);
  private toasterService = inject(ToasterService);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);

  private static readonly MAP_STROKE = '#0d5c42';
  private static readonly MAP_SELECTED = '#00d084';

  get title(): string {
    const d = this.resourceItemData;
    return (d?.label ?? d?.name ?? 'OGC Feature') || 'OGC Feature';
  }

  private olMap: OgcFeatureOlMap | null = null;
  activeDrawMode: DrawnGeometry['type'] | null = null;
  currentGeometry: DrawnGeometry | null = null;

  /** Apply Selection: fetch state */
  isApplying = false;
  applyError: string | null = null;
  featureCollection: OgcFeatureCollection | null = null;

  selectedFeature: OgcFeature | null = null;
  copiedToClipboard = false;

  private resizeObserver: ResizeObserver | null = null;


  get features(): OgcFeature[] {
    return this.featureCollection?.features ?? [];
  }

  get hasApiResponse(): boolean {
    return this.featureCollection !== null;
  }

  get selectedBbox(): { north: number; south: number; east: number; west: number } | null {
    if (!this.currentGeometry?.bbox || this.currentGeometry.bbox.length !== 4) return null;
    const [west, south, east, north] = this.currentGeometry.bbox;
    return { north, south, east, west };
  }

  ngAfterViewInit(): void {
    const el = this.featureMapRef?.nativeElement;
    if (!el) return;
    setTimeout(() => this.initMap(el), 0);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.olMap?.destroy();
    this.olMap = null;
  }

  private initMap(el: HTMLDivElement): void {
    this.olMap = new OgcFeatureOlMap({
      target: el,
      colors: {
        stroke: OgcFeatureDataComponent.MAP_STROKE,
        selected: OgcFeatureDataComponent.MAP_SELECTED,
      },
      onDrawn: (geometry) => {
        this.ngZone.run(() => {
          this.currentGeometry = geometry;
          this.applyError = null;
          this.cdr.detectChanges();
          void this.applySelection();
        });
      },
      onFeatureClick: (feature) => {
        this.ngZone.run(() => this.selectFeatureInZone(feature as OgcFeature));
      },
      onDrawModeChange: (mode) => {
        this.ngZone.run(() => {
          this.activeDrawMode = mode;
          this.cdr.detectChanges();
        });
      },
    });

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.olMap?.updateSize());
      this.resizeObserver.observe(el);
    }
  }

  startDrawPolygon(): void {
    this.olMap?.startDraw('polygon');
  }
  startDrawRectangle(): void {
    this.olMap?.startDraw('rectangle');
  }
  startDrawCircle(): void {
    this.olMap?.startDraw('circle');
  }

  async applySelection(): Promise<void> {
    if (!this.currentGeometry || !this.resourceItemData?.id) return;
    this.isApplying = true;
    this.applyError = null;
    this.featureCollection = null;
    this.olMap?.clearFeatures();
    this.cdr.markForCheck();

    const serverBaseUrl = this.resourceItemData.ogcServerBaseUrl;

    try {
      const token = await this.keycloak.getToken().catch(() => undefined);

      // Direct bbox query using OGC collections/items endpoint:
      // <catdomainname>/collections/<itemId>/items?bbox=<dynamicvalue>&bbox-crs=...
      let result: OgcFeatureCollection | null = null;

      if (token) {
        try {
          result = await this.ogcApi.fetchItemsByBbox(
            this.resourceItemData.id,
            this.currentGeometry.bbox,
            token,
            serverBaseUrl
          );
        } catch (authErr: any) {
          if (authErr?.status === 401 || authErr?.status === 403) {
            result = await this.ogcApi.getBbox(
              { bbox: this.currentGeometry.bbox },
              this.resourceItemData.id,
              undefined,
              serverBaseUrl
            );
          } else {
            throw authErr;
          }
        }
      } else {
        result = await this.ogcApi.getBbox(
          { bbox: this.currentGeometry.bbox },
          this.resourceItemData.id,
          undefined,
          serverBaseUrl
        );
      }

      if (result) {
        this.featureCollection = result;
        this.displayFeaturesOnMap(result.features ?? []);
        if (this.currentGeometry) {
          this.geometryDrawn.emit(this.currentGeometry);
        }
        this.hideDrawControl();
      } else {
        this.applyError = 'Failed to fetch OGC data. The collection may require authentication.';
      }
    } catch (err: any) {
      const msg = err?.error?.detail ?? err?.message ?? err?.statusText ?? 'Failed to fetch OGC data';
      this.applyError = typeof msg === 'string' ? msg : 'Failed to fetch OGC data';
    } finally {
      this.isApplying = false;
      this.cdr.markForCheck();
    }
  }

  getFeaturePropertyKeys(feature: OgcFeature): string[] {
    const p = feature?.properties;
    if (!p || typeof p !== 'object') return [];
    return Object.keys(p);
  }

  /** Download as JSON (feature collection). */
  downloadResults(): void {
    if (!this.featureCollection) return;
    const blob = new Blob([JSON.stringify(this.featureCollection, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ogc-features-${this.resourceItemData?.id ?? 'data'}-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    this.toasterService.success('JSON file downloaded successfully');
  }

  /** Download as GPKG (GeoPackage) via collection links – fetches href from getLink and downloads. */
  async downloadFeatures(): Promise<void> {
    const id = this.resourceItemData?.id;
    if (!id) {
      this.toasterService.error('Dataset ID is not available.');
      return;
    }
    try {
      const token = await this.keycloak.getToken().catch(() => undefined);
      const serverBaseUrl = this.resourceItemData?.ogcServerBaseUrl;
      const response = await this.ogcApi.getLink(id, token ?? undefined, serverBaseUrl);
      const links = response?.['links'] as Array<{ href?: string; rel?: string }> | undefined;
      if (!Array.isArray(links) || links.length === 0) {
        this.toasterService.error('No download link available. Downloading as JSON instead.');
        this.downloadResults();
        return;
      }
      // Prefer link with rel="enclosure" (IUDX style), otherwise use last link
      const enclosureLink = links.find((l) => String(l?.rel ?? '').toLowerCase() === 'enclosure');
      const linkToUse = enclosureLink ?? links[links.length - 1];
      const downloadUrl = linkToUse?.href;
      if (!downloadUrl) {
        this.toasterService.error('Download URL is missing. Downloading as JSON instead.');
        this.downloadResults();
        return;
      }
      const blob = await this.ogcApi.fetchBlobFromUrl(downloadUrl, token ?? undefined);
      const resourceLabel = this.resourceItemData?.label ?? this.resourceItemData?.id ?? 'ogc-features';
      const filename = `${String(resourceLabel).split(' ').join('_')}_${new Date().toLocaleDateString().split('/').join('')}.gpkg`;
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/geopackage+sqlite3' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      this.toasterService.success('File downloaded successfully');
    } catch (err) {
      console.error('Error downloading features:', err);
      this.toasterService.error('Failed to download features. Downloading as JSON instead.');
      this.downloadResults();
    }
  }

  closeModal(): void {
    this.closeRequested.emit();
  }

  formatBboxArray(): string {
    if (!this.currentGeometry?.bbox?.length) return '';
    return this.currentGeometry.bbox.map((n) => n.toFixed(6)).join(', ');
  }

  formatPropertiesJson(): string {
    if (!this.selectedFeature?.properties) return '{}';
    return JSON.stringify(this.selectedFeature.properties, null, 2);
  }

  async copyPropertiesJson(): Promise<void> {
    const json = this.formatPropertiesJson();
    try {
      await navigator.clipboard.writeText(json);
      this.copiedToClipboard = true;
      this.cdr.markForCheck();
      setTimeout(() => {
        this.copiedToClipboard = false;
        this.cdr.markForCheck();
      }, 2000);
    } catch {
      this.toasterService.error('Failed to copy to clipboard');
    }
  }

  /** Property entries for enhanced display */
  getPropertyEntries(): { key: string; value: unknown; type: 'email' | 'phone' | 'coord' | 'link' | 'null' | 'text' }[] {
    const p = this.selectedFeature?.properties;
    if (!p || typeof p !== 'object') return [];
    return Object.entries(p).map(([key, value]) => ({
      key,
      value,
      type: this.getPropertyType(key, value),
    }));
  }

  private getPropertyType(key: string, value: unknown): 'email' | 'phone' | 'coord' | 'link' | 'null' | 'text' {
    if (value == null) return 'null';
    const s = String(value).trim().toLowerCase();
    const k = key.toLowerCase();
    if (k.includes('email') && s.includes('@')) return 'email';
    if (k.includes('phone') || k.includes('tel')) return 'phone';
    if (k.includes('lat') || k.includes('lng') || k.includes('longitude')) return 'coord';
    if (s.startsWith('http://') || s.startsWith('https://')) return 'link';
    return 'text';
  }

  formatPropertyValue(value: unknown): string {
    if (value == null) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  clearDrawnShapes(): void {
    this.olMap?.clearDrawn();
    this.olMap?.clearFeatures();
    this.currentGeometry = null;
    this.applyError = null;
    this.featureCollection = null;
    this.selectedFeature = null;
    this.cdr.markForCheck();
  }

  /** Reset loaded data so the user can draw a new selection. */
  resetAndShowToolbar(): void {
    this.clearDrawnShapes();
    this.olMap?.resetView();
    this.olMap?.stopDraw();
    this.cdr.markForCheck();
  }

  closePropertiesPanel(): void {
    this.selectedFeature = null;
    this.copiedToClipboard = false;
    this.olMap?.setSelected(null);
    this.cdr.markForCheck();
  }

  private displayFeaturesOnMap(features: OgcFeature[]): void {
    this.olMap?.displayFeatures(features);
  }

  private selectFeatureInZone(feature: OgcFeature): void {
    this.selectedFeature = feature;
    this.copiedToClipboard = false;
    this.olMap?.setSelected(feature);
    this.cdr.markForCheck();
  }

  private hideDrawControl(): void {
    this.olMap?.stopDraw();
    this.cdr.markForCheck();
  }
}
